import { Link, Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarItem,
  SidebarSectionHeader,
} from "@norkron/web-sidebar";
import { api } from "../api";
import type { WorkspaceSummary } from "../api/types";
import {
  Clock,
  LayoutGrid,
  LibraryBig,
  ListChecks,
  PenLine,
  Repeat2,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Users,
} from "../compose/Icon";
import { Spinner } from "../compose/states";
import { useAuth } from "../auth/AuthContext";
import { useAsync } from "../lib/useAsync";
import { accentVars } from "../lib/accents";
import { useTheme } from "../theme/ThemeProvider";
import { SidebarLogo } from "./SidebarLogo";
import { UserMenu } from "./UserMenu";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import { NotificationBell } from "../notifications/NotificationBell";
import { OfflineIndicator } from "../sync/OfflineIndicator";
import { useSyncQueue } from "../sync/SyncProvider";

/**
 * The sidebar's default expanded width — the value the shell grid used to
 * hardcode as its second column (`3px 236px 1fr`). It is now the DEFAULT of a
 * resizable, persisted width, and the grid column tracks the element.
 */
const SIDEBAR_WIDTH = 236;

interface NavDef {
  key: string;
  label: string;
  icon: React.ReactNode;
  to: (slug: string) => string;
  badge?: (w: WorkspaceSummary) => number | undefined;
  /** Only rendered for internal (is_staff) users — the admin console. */
  staffOnly?: boolean;
}

interface NavGroup {
  /** Section label (design nav §), omitted for the article-unit spine home. */
  title?: string;
  items: NavDef[];
}

/**
 * Article-first nav spine (article-unit pivot §5). The article unit IS the spine:
 * `Articles` is the workspace home, standing alone above every supporting surface.
 * The document `Library` is DEMOTED into a secondary "Content" group — documents
 * are now resources INSIDE units, reachable but never the primary surface. The
 * collaboration + oversight tools group under "Workspace".
 */
const NAV_GROUPS: NavGroup[] = [
  {
    items: [
      {
        key: "articles",
        label: "Articles",
        icon: <LayoutGrid size={16} strokeWidth={1.5} />,
        to: (s) => `/w/${s}`,
      },
    ],
  },
  {
    title: "Content",
    items: [
      {
        key: "library",
        label: "Documents",
        icon: <LibraryBig size={16} strokeWidth={1.5} />,
        to: (s) => `/w/${s}/library`,
      },
    ],
  },
  {
    title: "Workspace",
    items: [
      {
        key: "tickets",
        label: "Tickets",
        icon: <ListChecks size={16} strokeWidth={1.5} />,
        to: (s) => `/w/${s}/tickets`,
      },
      {
        key: "proposals",
        label: "Proposals",
        icon: <Repeat2 size={16} strokeWidth={1.5} />,
        to: (s) => `/w/${s}/proposals`,
      },
      {
        key: "notes",
        label: "Notes",
        icon: <PenLine size={16} strokeWidth={1.5} />,
        to: (s) => `/w/${s}/notes`,
      },
      {
        key: "governance",
        label: "Governance",
        icon: <ShieldCheck size={16} strokeWidth={1.5} />,
        to: (s) => `/w/${s}/governance`,
      },
      {
        key: "activity",
        label: "Activity",
        icon: <Clock size={16} strokeWidth={1.5} />,
        to: (s) => `/w/${s}/activity`,
      },
      {
        key: "people",
        label: "People",
        icon: <Users size={16} strokeWidth={1.5} />,
        to: (s) => `/w/${s}/people`,
      },
      {
        key: "admin",
        label: "Admin",
        icon: <ShieldCheck size={16} strokeWidth={1.5} />,
        to: (s) => `/w/${s}/admin`,
        staffOnly: true,
      },
      {
        key: "settings",
        label: "Settings",
        icon: <SlidersHorizontal size={16} strokeWidth={1.5} />,
        to: (s) => `/w/${s}/settings`,
      },
    ],
  },
];

/**
 * The persistent three-zone frame for every /w/:ws route (ia-and-flows §1.1):
 * 3px accent spine · sidebar (switcher + nav + account) · main region. The
 * workspace accent is bound here via `data-workspace-accent` + accentVars, so
 * entering a workspace re-stains the spine, chip, focus rings and CTAs.
 *
 * ── THE SIDEBAR IS `@norkron/web-sidebar`, NOT AN `<aside>` ────────────────
 *
 * It used to be a hand-rolled `<aside className="shell__sidebar">` with
 * `.nav__group` / `.nav__section` / `.nav__item` rows. Everything elaborate
 * OWNS about that nav is still here — the `NAV_GROUPS` table, the `staffOnly`
 * filter, `isActive()`, the `data-workspace-accent` spine and the
 * deny-by-default `<Navigate to="/">` redirect. What the lib brings is the part
 * the hand-roll had none of:
 *
 *  * a **WCAG 2.4.1 skip link** as the first focusable child of the scroll
 *    region (bypass blocks: ten nav rows stood between a keyboard user and the
 *    document every single time);
 *  * **collapse to a 48px icon rail**, with `Cmd/Ctrl+B` and a persisted state;
 *  * a **keyboard-resizable** drag handle (Arrow/Home/End), width persisted;
 *  * a **mobile drawer** below 768px, where a 236px fixed grid column used to
 *    eat a third of a phone viewport;
 *  * `aria-current="page"` on the active row — the screen-reader half of "you
 *    are here", which `.nav__item--active` (a class) never carried.
 *
 * ⛔ `as={Link}` is load-bearing. `SidebarItem` renders a `<button>` by default
 * and an `<a href>` if given `href`; wrapping it in a router `<Link>` would emit
 * a nested anchor, and using `href` would make every nav click a FULL PAGE
 * RELOAD — remounting the SPA, the docz WASM and the auth session on every
 * navigation. `as={Link}` makes the row itself the router link, so exactly one
 * anchor is emitted and the ARIA lands on it.
 *
 * ⚠️ The lib's state classes are only real if `@norkron/web-sidebar/styles.css`
 * is imported (it self-injects nothing). `main.tsx` imports it, and
 * `AppShell.test.tsx` asserts the ACTIVE row's resolved paint differs from an
 * inactive one — because "the class is applied" and "the row looks active" are
 * different claims, and v0.3.x shipped the first without the second.
 */
export function AppShell() {
  const { slug } = useParams<{ slug: string }>();
  const { resolved } = useTheme();
  const { user } = useAuth();
  const location = useLocation();
  const syncQueue = useSyncQueue();
  const { data: workspaces, loading } = useAsync(
    () => api.listWorkspaces(),
    [],
  );

  const current =
    workspaces?.find((w) => w.slug === slug) ?? null;
  const accent = current?.accent ?? "cyanotype";

  // Deny-by-default (ia-and-flows §9 rule 1). `listWorkspaces` is server-scoped
  // to exactly the workspaces this user may open — staff get every workspace, a
  // client gets only theirs. Once that list has loaded, a `:slug` that isn't in
  // it is not this user's to see: redirect to the picker instead of letting the
  // inner screens 404 into a generic "Something went wrong" error. No foreign
  // workspace content is ever rendered. (While the list is still loading,
  // `workspaces` is undefined and we fall through to the spinner below.)
  if (slug && workspaces && !current) {
    return <Navigate to="/" replace />;
  }

  const isActive = (def: NavDef) => {
    if (!slug) return false;
    const path = location.pathname;
    const base = `/w/${slug}`;
    // Articles is the workspace index; it also owns the article workbench routes.
    if (def.key === "articles") {
      return path === base || path.startsWith(`${base}/article`);
    }
    // The document Library moved to /w/:slug/library; the single-doc viewer is
    // still a Library route.
    if (def.key === "library") {
      return (
        path.startsWith(`${base}/library`) || path.startsWith(`${base}/doc`)
      );
    }
    return path.startsWith(def.to(slug));
  };

  return (
    <div
      className="shell"
      data-workspace-accent={accent}
      style={accentVars(accent, resolved) as React.CSSProperties}
    >
      <div className="shell__spine" aria-hidden />
      <Sidebar
        // `complementary` is the lib's region role; the `<nav>` inside keeps the
        // navigation landmark the hand-roll had, so a screen-reader user still
        // gets "Workspace navigation" as a jump target.
        ariaLabel="Workspace sidebar"
        mobileDrawerLabel="Workspace navigation"
        side="start"
        // Icon rail rather than `'full'`: a zero-width sidebar leaves no way back
        // in on a surface whose only other toggle is a keyboard shortcut.
        collapsible="icon"
        // ⛔ `defaultWidth`, NEVER `width`. `width` is the CONTROLLED input: with
        // it supplied the lib stops mutating its own state, so the drag handle
        // drags nothing, its Arrow/Home/End keys do nothing, and `persistKey` has
        // nothing left to persist — silently, at exactly the right pixel width.
        // This shell hit that on first adoption; `defaultWidth` is the upstream
        // fix that came out of it (web-sidebar 0.5.0).
        defaultWidth={SIDEBAR_WIDTH}
        // Persistence routes through web-storage (TTL + cross-tab sync), so a
        // width set in one tab is not silently contradicted by another.
        persistKey="ela.shell.sidebar"
        className="shell__sidebar"
        testId="shell-sidebar"
      >
        <div className="shell__sidebar-inner">
          <SidebarLogo />
          <div className="notif-shell-row">
            <WorkspaceSwitcher current={current} workspaces={workspaces ?? []} />
            <NotificationBell />
          </div>
          {/* The scroll region owns the skip link; the wrapper exists because
              SidebarContent sets `height: 100%` inline, which needs a bounded
              flex parent rather than a bare column sibling. */}
          <div className="shell__sidebar-scroll">
            <SidebarContent testId="shell-sidebar-content">
              <nav className="nav" aria-label="Workspace">
                {slug
                  ? NAV_GROUPS.map((group, gi) => (
                      <div className="nav__group" key={group.title ?? `home-${gi}`}>
                        {group.title ? (
                          <SidebarSectionHeader label={group.title} />
                        ) : null}
                        {group.items
                          .filter((def) => !def.staffOnly || user?.is_staff)
                          .map((def) => (
                            <SidebarItem
                              key={def.key}
                              as={Link}
                              to={def.to(slug)}
                              label={def.label}
                              icon={def.icon}
                              // Declared on `NavDef` since the nav table was
                              // written and rendered by nothing until now.
                              badge={
                                current && def.badge ? def.badge(current) : undefined
                              }
                              active={isActive(def)}
                              testId={`nav-${def.key}`}
                            />
                          ))}
                      </div>
                    ))
                  : null}
                <div className="nav__spacer" />
                {slug ? (
                  <SidebarItem
                    as={Link}
                    to={`/w/${slug}/search`}
                    label="Search"
                    icon={<Search size={16} strokeWidth={1.5} />}
                    active={location.pathname.startsWith(`/w/${slug}/search`)}
                    testId="nav-search"
                  />
                ) : null}
              </nav>
            </SidebarContent>
          </div>
          <div className="nav__foot">
            <OfflineIndicator queue={syncQueue} />
            <UserMenu />
          </div>
        </div>
      </Sidebar>
      <main className="shell__main" id="shell-main">
        {loading && !current ? (
          <div style={{ paddingTop: 80 }}>
            <Spinner label="Loading workspace…" />
          </div>
        ) : (
          <Outlet context={{ current }} />
        )}
      </main>
    </div>
  );
}
